import {
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useIsTravelApp,
  wishlistsApi,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Avatar, Box, Divider, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import {
  AmenityIcon,
  GenericInfoPopup,
  getUserRatingQualifier,
  HomeSummary,
  Icon,
  IconName,
  PageTabNavigations,
  pluralize,
  ReviewIconTooltip,
  ReviewScore,
  useDeviceTypes,
  VacationRentalReviewsSection,
  isSpendAndGetOffer,
} from "halifax";
import H from "history";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  MODAL_ALERT,
  ModalCategoryType,
  ModalScreens,
  SelectedTravelOfferScreen,
  VIEWED_VR_DETAILS,
} from "redmond";
import {
  Amenity,
  ListingAddressEnum,
  ListingSearchResult,
} from "redmond/apis/tysons/vacation-rentals";
import clsx from "clsx";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  VR_COVER_REVIEWS_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { PATH_STAYS } from "../../../../utils/paths";
import { VacationRentalsAlertProperties } from "../../../common";
import { VacationRentalImportantInformation } from "../../../common/VacationRentalImportantInformation/component";
import { VacationRentalTerms } from "../../../common/VacationRentalTerms/component";
import { ConnectedDesktopRewardsHeader } from "../../../rewards/components/DesktopRewardsHeader/container";
import { ConnectedRewardsAccountSelection } from "../../../rewards/components/RewardsAccountSelection/container";
import { ConnectedWishlistManagementModal } from "../../../wishlist/components/WishlistManagementModal/container";
import { WishlistSnackbar } from "../../../wishlist/components/WishlistSnackbar/component";
import { CategorizedAmenities, VRMap } from "halifax";
import { ConnectedMobileShopHeader } from "../MobileShopHeader/container";
import { ConnectedPhotoGallery } from "../PhotoGallery/container";
import { RoomsAndBeds } from "halifax";
import { IShopCtaVariant } from "../ShopCta/component";
import { ConnectedShopCta } from "../ShopCta/container";
import { ShopSkeleton } from "../ShopSkeleton/component";
import { TruncatedText } from "../TruncatedText/component";
import {
  MOST_POPULAR_AMENITIES,
  RETURN_TO_SEARCH_MODAL_PRIMARY_BUTTON_TEXT,
  RETURN_TO_SEARCH_MODAL_SECONDARY_TEXT,
  VR_NOT_AVAILABLE_MODAL_TITLE,
} from "./constants";
import { VacationRentalShopConnectorProps } from "./container";
import "./styles.scss";
import { TravelWalletDetailsBanner } from "../../../travel-wallet/components";

export interface IShopProps
  extends VacationRentalShopConnectorProps,
    RouteComponentProps {}

export const Shop = (props: IShopProps) => {
  const {
    history,
    selectedListing,
    fetchedVacationRentalDetails,
    viewedVacationRentalDetailsProperties,
    fromDate,
    untilDate,
    setSelectedHomeIsFavorited,
  } = props;

  const expState = useExperiments();
  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const {
    data: favoritedLodgings,
    isSuccess: isFavoritedLodgingsSuccess,
    isError: isFavoritedLodgingsError,
  } = useSelector(wishlistsApi.endpoints.getAllWishlistItemsByUser.select());

  const isWishlistsLoaded =
    isFavoritedLodgingsSuccess || isFavoritedLodgingsError;

  const isFavorited = favoritedLodgings?.has(
    selectedListing?.listingId.id || ""
  );

  useEffect(() => {
    if (isWishlistExperiment && !isWishlistsLoaded) {
      return;
    }
    if (fetchedVacationRentalDetails) {
      trackEvent({
        eventName: VIEWED_VR_DETAILS,
        ...viewedVacationRentalDetailsProperties,
        properties: {
          ...viewedVacationRentalDetailsProperties.properties,
          ...(isWishlistExperiment && {
            is_favorited: isFavorited,
          }),
        },
      });
    }
  }, [fetchedVacationRentalDetails, isWishlistExperiment, isWishlistsLoaded]);

  // Keep track of the favorited status of the selected home for tracking in the booking flow
  useEffect(() => {
    if (
      isWishlistExperiment &&
      isWishlistsLoaded &&
      isFavorited !== undefined
    ) {
      setSelectedHomeIsFavorited(isFavorited);
    }
  }, [
    isWishlistExperiment,
    isWishlistsLoaded,
    isFavorited,
    setSelectedHomeIsFavorited,
  ]);

  useEffect(() => {
    // removes medallia from the fixed bottom that was covering pricing
    if (document && document.getElementById("nebula_div_btn")) {
      document!.getElementById("nebula_div_btn")!.style.display = "none";
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [document.getElementById("nebula_div_btn")]);

  if (!fetchedVacationRentalDetails) {
    return <ShopSkeleton />;
  }

  if (!selectedListing) {
    return (
      <ShopErrorModal
        history={history}
        fromDate={fromDate}
        untilDate={untilDate}
      />
    );
  }

  return (
    <ShopListingDetails selectedListing={selectedListing} history={history} />
  );
};

interface ShopErrorModalProps {
  history: H.History;
  selectedListing?: ListingSearchResult;
  fromDate?: Date | null;
  untilDate?: Date | null;
}

const ShopErrorModal = ({
  history,
  selectedListing,
  fromDate,
  untilDate,
}: ShopErrorModalProps) => {
  const { matchesMobile } = useDeviceTypes();

  const redirectToStays = () => {
    history.push(`${PATH_STAYS}`);
  };

  const modalEventProperties: VacationRentalsAlertProperties = {
    lob: "vacation rentals",
    type: "vacation_rentals_shop_error",
    modal_type: "vacation_rentals_shop_error",
    screen: ModalScreens.VR_DETAILS,
    primary_button: RETURN_TO_SEARCH_MODAL_PRIMARY_BUTTON_TEXT || "",
    secondary_button: "",
    category: ModalCategoryType.TROUBLE,
    home_id: selectedListing?.listingId.id,
    check_in_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : undefined,
    check_out_date: untilDate
      ? dayjs(untilDate).format("YYYY-MM-DD")
      : undefined,
    funnel: "vacation_rentals",
    step: "shop",
    modal_title: VR_NOT_AVAILABLE_MODAL_TITLE,
    modal_subtitle: RETURN_TO_SEARCH_MODAL_SECONDARY_TEXT,
    agent_subtitle: "",
    agent_title: "",
  };

  useEffect(() => {
    trackEvent({
      eventName: MODAL_ALERT,
      properties: modalEventProperties,
    });
  }, []);

  const buttons = [
    {
      buttonText: RETURN_TO_SEARCH_MODAL_PRIMARY_BUTTON_TEXT,
      defaultStyle: "h4r-primary",
      onClick: () => {
        redirectToStays();
      },
    },
  ];

  return (
    <GenericInfoPopup
      buttons={buttons}
      className="booking-error-modal"
      image={<Icon className="error-icon" name={IconName.ErrorState} />}
      isMobile={matchesMobile}
      open={true}
      title={VR_NOT_AVAILABLE_MODAL_TITLE}
      subtitle={RETURN_TO_SEARCH_MODAL_SECONDARY_TEXT}
      zIndex={matchesMobile ? 1301 : 1300}
    />
  );
};

interface ShopListingDetailsProps {
  selectedListing: ListingSearchResult;
  history: H.History;
}

const ShopListingDetails = (props: ShopListingDetailsProps) => {
  const { selectedListing, history } = props;

  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [isTabScrolling, setIsTabScrolling] = useState<boolean>(false);

  const expState = useExperiments();

  const showReviews =
    getExperimentVariant(expState.experiments, VR_COVER_REVIEWS_EXPERIMENT) ===
    AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const { availability, listing } = selectedListing;
  const { content, hostInfo, terms, reviews } = listing;
  const {
    name,
    description,
    amenities,
    layout,
    location,
    starRating,
    userRating,
    registrationId,
  } = content;
  const { bathrooms, bedrooms, numberOfBedrooms, numberOfBathrooms } = layout;

  const { hostName, hostImageUrl } = hostInfo || {};
  const { exactCoordinates } = location;

  const listingGoogleCoordinates = new google.maps.LatLng({
    lat: exactCoordinates.lat,
    lng: exactCoordinates.lon,
  });

  let city =
    content?.location?.address?.ListingAddress !== ListingAddressEnum.Empty
      ? content?.location?.address?.city
      : undefined;

  const {
    checkInTimes,
    checkOutTimes,
    checkinInformation,
    importantInformation,
    petsPolicy,
    petFriendly,
    maxOccupancy,
  } = terms;

  const { rate } = availability;

  const cancellationPolicy = rate?.cancellationPolicy?.policy;
  const paymentPolicy = rate?.paymentPolicy;

  const renderMaxOccupancyIconLabel = (maxOccupancy: number) => {
    return (
      maxOccupancy > 0 && (
        <Box display="flex" alignItems={"center"}>
          <Icon
            name={IconName.UserOutline}
            width="14px"
            height="14px"
            strokeWidth="0.5px"
            className="icon-stroke-dark"
          />
          <Typography
            className="icon-label"
            style={{
              color: "#141414",
              paddingLeft: "4px",
            }}
          >
            Up to {maxOccupancy} {pluralize(maxOccupancy, "guest", "guests")}
          </Typography>
        </Box>
      )
    );
  };

  const renderNumberOfBedroomsIconLabel = (numBedrooms: number) => {
    return (
      numBedrooms > 0 && (
        <Box display="flex" alignItems="center">
          <Icon
            name={IconName.BedOutlineIcon}
            width="16px"
            height="16px"
            className="icon-fill-dark"
          />

          <Typography
            className="icon-label"
            style={{ color: "#141414", paddingLeft: "4px" }}
          >
            {numBedrooms} {pluralize(numBedrooms, "bedroom", "bedrooms")}
          </Typography>
        </Box>
      )
    );
  };

  const renderNumberOfBathroomsIconLabel = (numBathrooms: number) => {
    return (
      numBathrooms > 0 && (
        <Box display="flex" alignItems="center">
          <Icon
            name={IconName.BathTubOutlineIcon}
            width="16px"
            height="16px"
            className="icon-stroke-dark"
          />
          <Typography className="icon-label" style={{ paddingLeft: "4px" }}>
            {numBathrooms} {pluralize(numBathrooms, "bathroom", "bathrooms")}
          </Typography>
        </Box>
      )
    );
  };

  const renderAmenityIconLabel = (amenity: Amenity) => {
    return (
      <Box display="flex" alignItems="flex-start" key={amenity.kind}>
        <AmenityIcon amenity={amenity.kind} className="icon-fill-dark" />
        <Typography
          style={{
            fontSize: "14px",
            color: "#141414",
            paddingLeft: "8px",
          }}
        >
          {amenity.displayName}
        </Typography>
      </Box>
    );
  };

  /** Navbar */
  const tabs = [
    {
      label: "Gallery",
      onClick: () => scrollToSection("gallery"),
    },

    { label: "Overview", onClick: () => scrollToSection("overview") },
    ...(description
      ? [
          {
            label: "About",
            onClick: () => scrollToSection("about"),
          },
        ]
      : []),
    {
      label: "Amenities",
      onClick: () => scrollToSection("amenities"),
    },
    ...(showReviews && reviews && reviews.length > 0
      ? [
          {
            label: "Reviews",
            onClick: () => scrollToSection("reviews"),
          },
        ]
      : []),
    {
      label: "Location",
      onClick: () => scrollToSection("location"),
    },
    {
      label: "House rules",
      onClick: () => scrollToSection("house-rules"),
    },
  ];

  const scrollToSection = (section: string) => {
    const elem = window.document.getElementById(section);
    if (elem) {
      setIsTabScrolling(true);

      const offset = 50 + 80 + 60 + 30; // 50 (banner) + 80 (toolbar) + 60 (navigation) + 30 (padding)
      const y = elem.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top: y, behavior: "smooth" });
      setTimeout(() => {
        setIsTabScrolling(false);
      }, 500);
    }
  };

  const [gallerySectionRef, gallerySectionInView] = useInView({
    threshold: 1,
    rootMargin: "0px 0px 500px 0px",
  });
  const [overviewSectionRef, overviewSectionInView] = useInView({
    threshold: 1,
    rootMargin: "0px 0px 500px 0px",
  });
  const [aboutSectionRef, aboutSectionInView] = useInView({
    threshold: 0.8,
    rootMargin: "0px 0px 500px 0px",
  });
  const [amenitiesSectionRef, amenitiesSectionInView] = useInView({
    threshold: 0.8,
  });
  const [reviewsSectionRef, reviewsSectionInView] = useInView({
    threshold: 0.8,
  });
  const [locationSectionRef, locationSectionInView] = useInView({
    threshold: 0.8,
  });
  const [houseRulesSectionRef, houseRulesSectionInView] = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (!isTabScrolling) {
      if (gallerySectionInView) {
        setSelectedTabIndex(0);
      } else if (overviewSectionInView) {
        setSelectedTabIndex(1);
      } else if (aboutSectionInView) {
        setSelectedTabIndex(2);
      } else if (amenitiesSectionInView) {
        setSelectedTabIndex(3);
      } else if (reviewsSectionInView) {
        setSelectedTabIndex(4);
      } else if (locationSectionInView) {
        setSelectedTabIndex(5);
      } else if (houseRulesSectionInView) {
        setSelectedTabIndex(6);
      }
    }
  }, [
    gallerySectionInView,
    overviewSectionInView,
    aboutSectionInView,
    amenitiesSectionInView,
    reviewsSectionInView,
    locationSectionInView,
    houseRulesSectionInView,
  ]);

  /** Sections */

  const renderOverview = (isMobile: boolean) => {
    return (
      <div id="overview" ref={overviewSectionRef}>
        <Box
          className="section"
          style={{
            ...(isMobile && { paddingLeft: 0, paddingRight: 0 }),
          }}
        >
          {isMobile && (
            <HomeSummary
              paddingBottom="15px"
              paddingX="15px"
              city={city}
              starRating={starRating}
              userRating={userRating?.score}
            />
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            paddingX={isMobile ? "20px" : "0px"}
          >
            <Typography
              className="title"
              {...(isXmodRedesign && { variant: isMobile ? "h6" : "h2" })}
            >
              {name}
            </Typography>
            {!isMobile && userRating && userRating?.score && (
              <Box display="flex" alignItems="baseline">
                <Box display="flex" alignItems="center">
                  <Typography style={{ color: "#141414" }}>
                    {getUserRatingQualifier(userRating?.score)}
                  </Typography>
                  <ReviewIconTooltip />
                  <ReviewScore score={userRating?.score} size="lg" />
                </Box>
              </Box>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            paddingBottom="20px"
            style={{ gap: "20px" }}
            paddingX={isMobile ? "20px" : "0px"}
          >
            {renderMaxOccupancyIconLabel(maxOccupancy)}
            {renderNumberOfBedroomsIconLabel(numberOfBedrooms)}
            {renderNumberOfBathroomsIconLabel(numberOfBathrooms)}
          </Box>
          {isMobile && (
            <>
              <Divider style={{ marginBottom: "20px" }} />
              <Box paddingX={isMobile ? "20px" : "0px"}>
                <Box display="flex" flexDirection="column" marginBottom="10px">
                  <ConnectedShopCta variant={IShopCtaVariant.PRICING_ONLY} />
                </Box>
              </Box>
            </>
          )}
          <Divider style={{ marginBottom: "20px" }} />
          <Typography
            variant="h6"
            style={{
              paddingBottom: "20px",
              ...(isMobile && { paddingLeft: "20px", paddingRight: "20px" }),
            }}
          >
            Most popular amenities
          </Typography>
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            style={{ gap: "20px" }}
            paddingX={isMobile ? "20px" : "0px"}
          >
            {amenities
              .filter((x) => MOST_POPULAR_AMENITIES.includes(x.kind))
              .map((y) => renderAmenityIconLabel(y))}
          </Box>
        </Box>
      </div>
    );
  };

  const renderAbout = () => {
    return (
      description && (
        <div id="about" ref={aboutSectionRef}>
          <Box className="section">
            <Typography
              className="title"
              {...(isXmodRedesign && { variant: matchesMobile ? "h6" : "h2" })}
            >
              About
            </Typography>
            <Box display="flex" flexDirection="column" style={{ gap: "20px" }}>
              {hostName && (
                <Box display="flex" alignItems="center" style={{ gap: "15px" }}>
                  {hostImageUrl && <Avatar src={hostImageUrl} />}
                  <Typography variant="h6" style={{ color: "#141414" }}>
                    {hostName ? `Hosted by ${hostName}` : "Your host"}
                  </Typography>
                </Box>
              )}
              <TruncatedText text={description} limit={500} setAsHTML={true} />
            </Box>
          </Box>
        </div>
      )
    );
  };

  const renderRoomsAndBeds = () => {
    return (
      bedrooms.length > 0 && (
        <div id="rooms-and-beds">
          <Box className="section">
            <Typography
              className="title"
              {...(isXmodRedesign && { variant: matchesMobile ? "h6" : "h2" })}
            >
              Rooms and beds
            </Typography>
            <Box paddingBottom="20px">
              <RoomsAndBeds
                bedrooms={bedrooms}
                bathrooms={bathrooms}
                className="vacation-rental-rooms-and-beds"
              />
            </Box>
          </Box>
        </div>
      )
    );
  };

  const renderAmenities = () => {
    return (
      <div id="amenities" ref={amenitiesSectionRef}>
        <Box className="section">
          <Typography
            className="title"
            {...(isXmodRedesign && { variant: matchesMobile ? "h6" : "h2" })}
          >
            Amenities and features
          </Typography>
          <CategorizedAmenities
            amenities={amenities}
            className="vr-categorized-amenities"
          />
        </Box>
      </div>
    );
  };

  const renderReviews = () => {
    return (
      showReviews &&
      reviews &&
      reviews.length > 0 && (
        <div id="reviews" ref={reviewsSectionRef}>
          <Box className="section" position="relative">
            <Typography
              className="title"
              {...(isXmodRedesign && { variant: matchesMobile ? "h6" : "h2" })}
            >
              Customer Reviews
            </Typography>
            <VacationRentalReviewsSection
              score={userRating?.score}
              reviews={reviews}
              isXmodRedesign={isXmodRedesign}
            />
          </Box>
        </div>
      )
    );
  };

  const renderLocation = () => {
    return (
      <div id="location" ref={locationSectionRef}>
        <Box className="section" position="relative" zIndex={0}>
          <Typography
            className="title"
            {...(isXmodRedesign && { variant: matchesMobile ? "h6" : "h2" })}
          >
            Location
          </Typography>
          <VRMap
            listingName={name}
            location={location}
            coordinates={listingGoogleCoordinates}
            matchesMobile={matchesMobile}
            isXmodRedesign={isXmodRedesign}
          />
        </Box>
      </div>
    );
  };

  const renderHouseRules = () => {
    return (
      <div id="house-rules" ref={houseRulesSectionRef}>
        <VacationRentalTerms
          checkinInformation={checkinInformation}
          checkInTimes={checkInTimes}
          checkOutTimes={checkOutTimes}
          petFriendly={petFriendly}
          petsPolicy={petsPolicy}
          cancellationPolicy={cancellationPolicy}
          paymentPolicy={paymentPolicy}
        />
      </div>
    );
  };

  const renderImportantInformation = () => {
    return (
      (importantInformation || registrationId) && (
        <Box>
          <VacationRentalImportantInformation
            importantInformation={importantInformation}
            registrationId={registrationId}
          />
        </Box>
      )
    );
  };

  const renderOfferBanner = () =>
    !isSpendAndGetOffer(selectedListing.bestOffer?.descriptions ?? []) && (
      <TravelWalletDetailsBanner
        offer={selectedListing.bestOffer}
        showButton={false}
        className="hotel-shop-offer"
        screen={SelectedTravelOfferScreen.HOMES_SHOP}
        {...props}
      />
    );

  const { isTravelApp } = useIsTravelApp();

  return (
    <Box
      className={clsx("vacation-rental-shop", {
        "travel-app": isTravelApp,
        xmod: isXmodRedesign,
      })}
      style={{ backgroundColor: "white" }}
    >
      {/* Mobile */}
      {matchesMobile && (
        <Box className="shop-mobile" display="flex" flexDirection="column">
          <ConnectedMobileShopHeader />
          <Box
            padding="18px"
            style={{ backgroundColor: "#FBFBFB" }}
            borderBottom="0.5px solid var(--grey-11)"
          >
            <ConnectedRewardsAccountSelection />
          </Box>
          <Box id="gallery">
            <ConnectedPhotoGallery />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            padding="20px"
            style={{ backgroundColor: "#FBFBFB" }}
          >
            {renderOfferBanner()}
            {renderOverview(true)}
            {renderAbout()}
            {renderRoomsAndBeds()}
            {renderAmenities()}
            {renderReviews()}
            {renderLocation()}
            {renderHouseRules()}
            {renderImportantInformation()}
          </Box>
          <Box
            position="fixed"
            bottom="0px"
            boxShadow="0px -2px 10px rgba(0, 0, 0, 0.2)"
            width="100%"
          >
            <ConnectedShopCta variant={IShopCtaVariant.MOBILE_CTA_BUTTON} />
          </Box>
        </Box>
      )}

      {/* Desktop */}
      {matchesDesktop && (
        <Box className="shop-desktop" display="flex" flexDirection="column">
          <ConnectedDesktopRewardsHeader />
          <div ref={gallerySectionRef} id="gallery">
            <Box paddingX="120px">
              <ConnectedPhotoGallery />
            </Box>
          </div>
          <PageTabNavigations
            className="navigation-overrides"
            navItems={tabs}
            selectedTab={selectedTabIndex}
            rightContent={<></>}
          />
          <Box
            display="flex"
            flexDirection="row"
            style={{ gap: "20px", backgroundColor: "#FBFBFB" }}
            paddingX="120px"
            paddingY="30px"
          >
            <Box display="flex" flexDirection="column">
              {renderOverview(false)}
              {renderAbout()}
              {renderRoomsAndBeds()}
              {renderAmenities()}
              {renderReviews()}
              {renderLocation()}
              {renderHouseRules()}
              {renderImportantInformation()}
            </Box>
            <Box className="vacation-rental-shop-cta-container">
              <ConnectedShopCta variant={IShopCtaVariant.DESKTOP} />
              {renderOfferBanner()}
            </Box>
          </Box>
        </Box>
      )}

      <ConnectedWishlistManagementModal history={history} entryType="vr_shop" />
      <WishlistSnackbar />
    </Box>
  );
};
